import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { PayrollService } from '../../services/payroll.service';
import { ISalaryComponent } from '../../interfaces/payroll.interface';
import { useToast } from '../../context/ToastContext';
import SalaryComponentForm from './SalaryComponentForm';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import SearchableSelect from '../common/SearchableSelect';

const SalaryComponentList: React.FC = () => {
    const [components, setComponents] = useState<ISalaryComponent[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingComponent, setEditingComponent] = useState<ISalaryComponent | null>(null);
    const { showSuccess, showApiError } = useToast();
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();

    const loadComponents = async () => {
        if (!institutionId) return;
        try {
            const data = await PayrollService.getSalaryComponents(institutionId);
            setComponents(data);
        } catch (error) {
            showApiError(error, 'Failed to load salary components');
        }
    };

    useEffect(() => { loadComponents(); }, [institutionId]);

    const handleAdd = () => {
        setEditingComponent(null);
        setDialogOpen(true);
    };

    const handleEdit = (component: ISalaryComponent) => {
        setEditingComponent(component);
        setDialogOpen(true);
    };

    const handleDelete = async (id: string) => {
        try {
            await PayrollService.deleteSalaryComponent(id);
            showSuccess('Component deleted');
            loadComponents();
        } catch (error) {
            showApiError(error, 'Failed to delete component');
        }
    };

    const handleSeedDefaults = async () => {
        try {
            await PayrollService.seedDefaultSalaryComponents(institutionId);
            showSuccess('Default components seeded');
            loadComponents();
        } catch (error) {
            showApiError(error, 'Failed to seed defaults');
        }
    };

    const handleSave = async (data: Partial<ISalaryComponent>) => {
        try {
            if (editingComponent) {
                await PayrollService.updateSalaryComponent(editingComponent.id, data);
                showSuccess('Component updated');
            } else {
                await PayrollService.createSalaryComponent(data, institutionId);
                showSuccess('Component created');
            }
            setDialogOpen(false);
            loadComponents();
        } catch (error) {
            showApiError(error, 'Failed to save component');
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="payroll.components.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Salary Components</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {isSystemAdmin && (
                        <SearchableSelect
                            data-help="payroll.components.institution"
                            label="Select Institution"
                            options={institutions}
                            value={institutionId}
                            onChange={id => setInstitutionId(id)}
                            getOptionLabel={inst => inst.name}
                            clearable={false}
                            sx={{ minWidth: 220 }}
                        />
                    )}
                    {isSystemAdmin && (
                        <Button data-help="payroll.components.seedDefaults" variant="outlined" onClick={handleSeedDefaults} disabled={!institutionId}>
                            Seed Defaults
                        </Button>
                    )}
                    <Button data-help="payroll.components.add" variant="contained" color="primary" onClick={handleAdd} disabled={!institutionId}>
                        Add Component
                    </Button>
                </Box>
            </Box>

            <TableContainer component={Paper} data-help-section="payroll.components.table">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="payroll.components.col.name">Name</TableCell>
                            <TableCell data-help="payroll.components.col.type">Type</TableCell>
                            <TableCell data-help="payroll.components.col.status">Status</TableCell>
                            <TableCell align="right" data-help="payroll.components.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {components.map((comp) => (
                            <TableRow key={comp.id}>
                                <TableCell>
                                    {comp.name}
                                    {comp.isOvertimeBase && <Chip data-help="payroll.components.flag.otBase" size="small" label="OT base" sx={{ ml: 1 }} />}
                                    {comp.isOvertimeEarning && <Chip data-help="payroll.components.flag.otEarning" size="small" label="OT earning" sx={{ ml: 1 }} />}
                                    {comp.isBonusEarning && <Chip data-help="payroll.components.flag.bonusEarning" size="small" label="Bonus earning" sx={{ ml: 1 }} />}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={comp.type}
                                        color={comp.type === 'EARNING' ? 'success' : 'error'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={comp.isActive ? 'Active' : 'Inactive'}
                                        color={comp.isActive ? 'primary' : 'default'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton data-help="payroll.components.action.edit" onClick={() => handleEdit(comp)} size="small" aria-label="edit">
                                        <EditIcon />
                                    </IconButton>
                                    {isSystemAdmin && (
                                        <IconButton data-help="payroll.components.action.delete" onClick={() => handleDelete(comp.id)} size="small" color="error" aria-label="delete">
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {components.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">No salary components found</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <SalaryComponentForm
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSave={handleSave}
                component={editingComponent}
            />
        </Box>
    );
};

export default SalaryComponentList;
